// SAD-186: project closeout checklist page. A deliberate clone of
// ActivationGatePage/ActivationGateTab's shape — local per-row edit
// staging, a Save button per row, and a completion CTA that surfaces
// violations inline instead of throwing. Unlike the gate, Closeout is
// always visible: every program can be closed out, so this page never
// redirects away.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraCheckbox, SpecraCombobox, SpecraTextarea, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Check, Download } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageBody } from '../../../../components/layout';
import { useAuth } from '../../../../contexts/authContext';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { ActionGate, programRef } from '../../../../lib/access';
import { ConfirmDeleteDialog } from '../../_shared/components/ConfirmDeleteDialog';
import { useAnswerCloseoutChecklistItem, useCloseoutChecklist, useCloseProgram, useCompleteCloseoutChecklist, useDeleteCloseoutOutstandingItem, useExportAndDownloadCloseout, useExportCloseoutChecklist, useStartCloseoutChecklist, useUpsertCloseoutOutstandingItem } from '../closeoutHooks';
import { programRoleAssignmentHooks } from '../relationshipHooks';
import { capitalizeSection, closeoutItemStatus, closeoutSdsBullets, responsiblePartyOptions, sdsSectionKeys } from './closeoutFormat';
const ANSWERS = [
    'YES',
    'N_A',
    'NO'
];
const ANSWER_LABEL = {
    YES: 'Yes',
    N_A: 'N/A',
    NO: 'No'
};
const ANSWER_TESTID = {
    YES: Testids.closeout.answerYesButton,
    N_A: Testids.closeout.answerNaButton,
    NO: Testids.closeout.answerNoButton
};
const SPECRA_GROUP_NOTE = 'These checks confirm the final SDS record is complete, accurate, and useful for future delivery review. They do not require creation of a Growth opportunity, proposal, or past-performance package before the project can close.';
function dateInputValue(ts) {
    return ts ? ts.toDate().toISOString().slice(0, 10) : '';
}
function toTimestamp(ymd) {
    return ymd ? Timestamp.fromDate(new Date(`${ymd}T00:00:00Z`)) : undefined;
}
export function CloseoutPage() {
    const { programId, program } = useProgramContext();
    const { data: checklistResp, isLoading, error } = useCloseoutChecklist(programId);
    const start = useStartCloseoutChecklist();
    const complete = useCompleteCloseoutChecklist();
    const closeProgram = useCloseProgram();
    const exportChecklist = useExportCloseoutChecklist();
    const [lastViolations, setLastViolations] = useState(null);
    const [closeDialogOpen, setCloseDialogOpen] = useState(false);
    const isClosed = !!program?.closedAt;
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx("div", {
                className: "text-muted-foreground",
                children: "Loading closeout checklist…"
            })
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsxs("div", {
                className: "text-destructive",
                children: [
                    "Failed to load closeout checklist:",
                    ' ',
                    error instanceof Error ? error.message : 'Unknown error'
                ]
            })
        });
    }
    const resource = programRef(programId);
    if (!checklistResp) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.closeout.page,
                className: "space-y-4",
                children: /*#__PURE__*/ _jsxs(SpecraCard, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraCardHeader, {
                            children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                                children: "Project Closeout"
                            })
                        }),
                        /*#__PURE__*/ _jsxs(SpecraCardContent, {
                            className: "space-y-3",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: "No closeout checklist yet — start one to work through the sign-off items before this program can close."
                                }),
                                /*#__PURE__*/ _jsx(ActionGate, {
                                    action: "update",
                                    resource: resource,
                                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                                        onClick: ()=>start.mutate({
                                                programId
                                            }),
                                        disabled: start.isPending,
                                        "data-testid": Testids.closeout.startButton,
                                        children: "Start Closeout"
                                    })
                                })
                            ]
                        })
                    ]
                })
            })
        });
    }
    const checklist = checklistResp.checklist;
    const items = checklistResp.items ?? [];
    const outstandingItems = checklistResp.outstandingItems ?? [];
    const checklistId = checklist?.id ?? '';
    const checklistState = checklist?.state ?? 'Pending';
    const isCompleted = checklistState === 'Completed';
    const writesLocked = isCompleted || isClosed;
    const standardItems = items.filter((it)=>!it.specraSpecific).sort((a, b)=>a.ordinal - b.ordinal);
    const specraItems = items.filter((it)=>it.specraSpecific).sort((a, b)=>a.ordinal - b.ordinal);
    const answeredCount = items.filter((it)=>it.answer).length;
    // Same source the checklist items' own SDS mapping comes from, so the
    // Outstanding table's SDS dropdown can never drift from it.
    const availableSdsSections = Array.from(new Set(items.flatMap((it)=>sdsSectionKeys(it.sdsSection)))).sort();
    const handleComplete = async ()=>{
        try {
            const resp = await complete.mutateAsync({
                programId
            });
            if (resp.violations && resp.violations.length > 0) {
                setLastViolations(resp.violations);
            } else {
                setLastViolations(null);
            }
        } catch  {
        // toast handled by hook
        }
    };
    return /*#__PURE__*/ _jsx(PageBody, {
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.closeout.page,
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsxs(SpecraCard, {
                    "data-testid": Testids.closeout.header,
                    "data-state": checklistState,
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                            className: "flex flex-row items-start justify-between gap-4",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsxs(SpecraCardTitle, {
                                            className: "flex items-center gap-3",
                                            children: [
                                                "Project Closeout",
                                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                                    variant: isCompleted ? 'success' : 'outline',
                                                    children: checklistState
                                                }),
                                                isClosed && /*#__PURE__*/ _jsx(SpecraBadge, {
                                                    variant: "neutral",
                                                    children: "Closed"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "text-sm text-muted-foreground mt-1",
                                            "data-testid": Testids.closeout.summary,
                                            children: [
                                                answeredCount,
                                                " of ",
                                                items.length,
                                                " items answered · ",
                                                outstandingItems.length,
                                                ' ',
                                                "outstanding item(s)"
                                            ]
                                        })
                                    ]
                                }),
                                isCompleted && /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(ActionGate, {
                                            action: "update",
                                            resource: resource,
                                            children: /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "outline",
                                                onClick: ()=>exportChecklist.mutate({
                                                        programId
                                                    }),
                                                disabled: exportChecklist.isPending,
                                                "data-testid": Testids.closeout.exportButton,
                                                children: "Export Checklist"
                                            })
                                        }),
                                        !isClosed && /*#__PURE__*/ _jsx(ActionGate, {
                                            action: "close",
                                            resource: resource,
                                            children: /*#__PURE__*/ _jsx(SpecraButton, {
                                                onClick: ()=>setCloseDialogOpen(true),
                                                disabled: closeProgram.isPending,
                                                "data-testid": Testids.closeout.closeButton,
                                                children: "Mark Program Closed"
                                            })
                                        })
                                    ]
                                }),
                                !isCompleted && /*#__PURE__*/ _jsx(ActionGate, {
                                    action: "update",
                                    resource: resource,
                                    alsoDisabled: writesLocked,
                                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                                        onClick: handleComplete,
                                        disabled: complete.isPending,
                                        "data-testid": Testids.closeout.completeButton,
                                        children: "Complete Checklist"
                                    })
                                })
                            ]
                        }),
                        isClosed && /*#__PURE__*/ _jsx(SpecraCardContent, {
                            children: /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.closeout.closedBanner,
                                className: "rounded-md border bg-muted/50 p-3 text-sm text-muted-foreground",
                                children: [
                                    "Program closed",
                                    program?.closedBy ? ` by ${program.closedBy}` : '',
                                    program?.closedAt ? ` on ${new Date(Number(program.closedAt.seconds) * 1000).toLocaleDateString()}` : '',
                                    ". The checklist is read-only."
                                ]
                            })
                        }),
                        lastViolations && lastViolations.length > 0 && /*#__PURE__*/ _jsx(SpecraCardContent, {
                            children: /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.closeout.violationsList,
                                className: "rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "font-medium text-destructive",
                                        children: "Cannot complete — the following items need an answer or comments:"
                                    }),
                                    /*#__PURE__*/ _jsx("ul", {
                                        className: "mt-1 list-disc pl-5 text-destructive",
                                        children: lastViolations.map((ordinal)=>{
                                            const item = items.find((it)=>it.ordinal === ordinal);
                                            return /*#__PURE__*/ _jsxs("li", {
                                                children: [
                                                    "Item ",
                                                    ordinal,
                                                    item ? `: ${item.text}` : ''
                                                ]
                                            }, ordinal);
                                        })
                                    })
                                ]
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(ItemGroup, {
                    heading: "Standard Closeout",
                    items: standardItems,
                    programId: programId,
                    checklistId: checklistId,
                    locked: writesLocked,
                    resource: resource
                }),
                /*#__PURE__*/ _jsx(ItemGroup, {
                    heading: "Additional Specra-specific closeout checks",
                    note: SPECRA_GROUP_NOTE,
                    items: specraItems,
                    programId: programId,
                    checklistId: checklistId,
                    locked: writesLocked,
                    resource: resource
                }),
                /*#__PURE__*/ _jsx(OutstandingItemsTable, {
                    programId: programId,
                    items: outstandingItems,
                    checklistItems: items,
                    availableSdsSections: availableSdsSections,
                    locked: writesLocked,
                    resource: resource
                }),
                /*#__PURE__*/ _jsx(CloseProgramDialog, {
                    open: closeDialogOpen,
                    onOpenChange: setCloseDialogOpen,
                    programId: programId,
                    onConfirmClose: ()=>closeProgram.mutate({
                            programId
                        })
                })
            ]
        })
    });
}
function ItemGroup({ heading, note, items, programId, checklistId, locked, resource }) {
    if (items.length === 0) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-base font-medium",
                        "data-testid": Testids.closeout.groupHeading,
                        "data-group": heading,
                        children: heading
                    }),
                    note && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground mt-1",
                        children: note
                    })
                ]
            }),
            items.map((item)=>/*#__PURE__*/ _jsx(ChecklistItemRow, {
                    item: item,
                    programId: programId,
                    checklistId: checklistId,
                    locked: locked,
                    resource: resource
                }, item.ordinal))
        ]
    });
}
function ChecklistItemRow({ item, programId, checklistId, locked, resource }) {
    const answerItem = useAnswerCloseoutChecklistItem();
    const [draft, setDraft] = useState({
        answer: item.answer,
        comments: item.comments
    });
    const dirty = draft.answer !== item.answer || draft.comments !== item.comments;
    // Saved-state truth (matt round-3 #7): status is derived from the PERSISTED
    // answer/comments (what the completion gate actually sees), with dirty edits
    // called out so a picked-but-unsaved row can't masquerade as done.
    const status = closeoutItemStatus({
        answer: item.answer,
        comments: item.comments
    }, {
        answer: draft.answer,
        comments: draft.comments
    });
    const sdsBullets = closeoutSdsBullets(item.sdsSection, item.reviewInstruction);
    const handleSave = async ()=>{
        try {
            await answerItem.mutateAsync({
                programId,
                checklistId,
                ordinal: item.ordinal,
                answer: draft.answer,
                comments: draft.comments
            });
        } catch  {
        // toast handled by hook
        }
    };
    return /*#__PURE__*/ _jsx(SpecraCard, {
        "data-testid": Testids.closeout.itemRow,
        "data-ordinal": item.ordinal,
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex-1",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "font-medium",
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "mr-1.5 tabular-nums text-muted-foreground",
                                    children: [
                                        item.ordinal,
                                        "."
                                    ]
                                }),
                                item.text
                            ]
                        }),
                        item.accountableParty && /*#__PURE__*/ _jsxs("div", {
                            className: "text-xs text-muted-foreground mt-1",
                            children: [
                                "Accountable: ",
                                item.accountableParty
                            ]
                        }),
                        sdsBullets.length > 0 && /*#__PURE__*/ _jsxs("div", {
                            className: "mt-1",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground",
                                    children: "Relevant Sections"
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    className: "mt-0.5 list-disc pl-4 text-xs",
                                    "data-testid": Testids.closeout.itemSdsLink,
                                    "data-ordinal": item.ordinal,
                                    children: sdsBullets.map((bullet, i)=>/*#__PURE__*/ _jsx("li", {
                                            children: /*#__PURE__*/ _jsx(Link, {
                                                to: `/delivery/programs/${programId}/${bullet.section}`,
                                                className: "text-primary underline",
                                                children: bullet.text
                                            })
                                        }, `${bullet.section}-${i}`))
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex w-full flex-col gap-2 sm:w-80",
                    children: [
                        /*#__PURE__*/ _jsxs("fieldset", {
                            "data-testid": Testids.closeout.answerControl,
                            "data-ordinal": item.ordinal,
                            className: "contents",
                            children: [
                                /*#__PURE__*/ _jsx("legend", {
                                    className: "sr-only",
                                    children: "Answer"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex gap-1",
                                    children: ANSWERS.map((a)=>{
                                        const selected = draft.answer === a;
                                        return /*#__PURE__*/ _jsx(SpecraButton, {
                                            type: "button",
                                            size: "sm",
                                            variant: selected ? 'default' : 'secondary',
                                            "aria-pressed": selected,
                                            disabled: locked,
                                            iconLeft: selected ? /*#__PURE__*/ _jsx(Check, {}) : undefined,
                                            onClick: ()=>setDraft((prev)=>({
                                                        ...prev,
                                                        answer: a
                                                    })),
                                            "data-testid": ANSWER_TESTID[a],
                                            children: ANSWER_LABEL[a]
                                        }, a);
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                            value: draft.comments,
                            onChange: (e)=>setDraft((prev)=>({
                                        ...prev,
                                        comments: e.target.value
                                    })),
                            placeholder: "Comments",
                            rows: 2,
                            disabled: locked,
                            "data-testid": Testids.closeout.commentsInput,
                            "data-ordinal": item.ordinal
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(ChecklistItemStatusBadge, {
                                    status: status
                                }),
                                /*#__PURE__*/ _jsx(ActionGate, {
                                    action: "update",
                                    resource: resource,
                                    alsoDisabled: locked,
                                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "secondary",
                                        onClick: handleSave,
                                        disabled: !dirty || answerItem.isPending,
                                        "data-testid": Testids.closeout.saveItemButton,
                                        children: "Save"
                                    })
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
// Per-item saved-state badge. "Saved" (success) marks a row the completion
// gate will accept; "Unsaved changes" (the fix for matt round-3 #7) warns that
// a picked answer or edited comment hasn't been persisted yet, so it still
// reads as incomplete to Complete Checklist; "Needs comment" flags a saved
// N/A or No missing its required comment; "Not answered" is the empty default.
const STATUS_BADGE = {
    saved: {
        variant: 'success',
        label: 'Saved'
    },
    unsaved: {
        variant: 'warning',
        label: 'Unsaved changes'
    },
    'needs-comment': {
        variant: 'warning',
        label: 'Needs comment'
    },
    unanswered: {
        variant: 'neutral',
        label: 'Not answered'
    }
};
function ChecklistItemStatusBadge({ status }) {
    const meta = STATUS_BADGE[status];
    return /*#__PURE__*/ _jsxs(SpecraBadge, {
        variant: meta.variant,
        "data-testid": Testids.closeout.itemStatus,
        "data-status": status,
        children: [
            status === 'saved' && /*#__PURE__*/ _jsx(Check, {
                className: "h-3 w-3"
            }),
            meta.label
        ]
    });
}
const EMPTY_DRAFT = {
    item: '',
    plannedCompletionDate: '',
    comments: '',
    sdsSection: '',
    responsibleParty: '',
    resolved: false
};
// Sentinel values for the two "pick to populate a free-text field" dropdowns
// below — Radix Select rejects an empty-string item value, and both fields
// stay editable after a pick, so a sentinel just clears/no-ops rather than
// writing a literal placeholder into the field.
const NO_SDS_SECTION = '__none__';
function draftFromItem(item) {
    return {
        item: item.item,
        plannedCompletionDate: dateInputValue(item.plannedCompletionDate),
        comments: item.comments,
        sdsSection: item.sdsSection,
        responsibleParty: item.responsibleParty,
        resolved: item.resolved
    };
}
function checklistItemLabel(item) {
    return `${item.ordinal}. ${item.text}`;
}
// Single-box Item control (matt round-3 #4): one combobox that both selects a
// program checklist item and accepts free-typed text, replacing the previous
// dropdown-then-separate-box pair matt flagged as reading poorly platform-wide.
// Picking an item fills the box with its "N. text" label; typing something not
// on the checklist commits verbatim via the combobox's create row, since an
// outstanding row is allowed to describe anything, on the checklist or not.
function ItemField({ value, onChange, checklistItems, disabled }) {
    const options = checklistItems.map((it)=>{
        const label = checklistItemLabel(it);
        return {
            value: label,
            label
        };
    });
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.closeout.outstandingItemCombobox,
        children: /*#__PURE__*/ _jsx(SpecraCombobox, {
            label: "Outstanding item",
            options: options,
            value: value || undefined,
            fallbackLabel: value || undefined,
            onValueChange: (next)=>onChange(next ?? ''),
            onCreate: (query)=>onChange(query),
            createLabel: (query)=>`Use “${query}”`,
            placeholder: "Select or type an item…",
            searchPlaceholder: "Search checklist items, or type your own…",
            emptyMessage: "No matching checklist item — type to use your own.",
            clearable: true,
            disabled: disabled,
            triggerProps: {
                className: 'h-8 text-xs'
            }
        })
    });
}
// Responsible Party control (matt round-3 #5): a combobox of the program's
// Roles roster plus the current user, resolved via responsiblePartyOptions,
// replacing the free-text input. The stored value stays the person's display
// name (the export renders it verbatim), and fallbackLabel preserves any
// legacy free-text value already on a row so saving never drops it.
function ResponsiblePartyField({ value, onChange, options, disabled }) {
    const comboOptions = options.map((o)=>({
            value: o.name,
            label: o.name,
            description: o.email || undefined
        }));
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.closeout.responsiblePartyCombobox,
        children: /*#__PURE__*/ _jsx(SpecraCombobox, {
            label: "Responsible party",
            options: comboOptions,
            value: value || undefined,
            fallbackLabel: value || undefined,
            onValueChange: (next)=>onChange(next ?? ''),
            placeholder: "Assign…",
            searchPlaceholder: "Search program roles…",
            emptyMessage: "No program member matches.",
            clearable: true,
            disabled: disabled,
            triggerProps: {
                className: 'h-8 text-xs'
            }
        })
    });
}
// Shared control for the Outstanding table's "SDS Link" cell: a dropdown
// built from the checklist items' own sds_section values (see
// availableSdsSections in CloseoutPage) so the two lists cannot drift apart.
// The row's current value is always included even if it's since fallen out
// of that list (stale/legacy data), so saving never silently drops it.
function SdsSectionField({ programId, value, onChange, availableSdsSections, disabled }) {
    const options = value && !availableSdsSections.includes(value) ? [
        value,
        ...availableSdsSections
    ] : availableSdsSections;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-1",
        children: [
            /*#__PURE__*/ _jsxs(Select, {
                value: value || NO_SDS_SECTION,
                onValueChange: (next)=>onChange(next === NO_SDS_SECTION ? '' : next),
                disabled: disabled,
                children: [
                    /*#__PURE__*/ _jsx(SelectTrigger, {
                        className: "h-8",
                        "data-testid": Testids.closeout.outstandingSdsSelect,
                        children: /*#__PURE__*/ _jsx(SelectValue, {
                            placeholder: "No SDS section"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(SelectContent, {
                        children: [
                            /*#__PURE__*/ _jsx(SelectItem, {
                                value: NO_SDS_SECTION,
                                children: "No SDS section"
                            }),
                            options.map((section)=>/*#__PURE__*/ _jsx(SelectItem, {
                                    value: section,
                                    children: capitalizeSection(section)
                                }, section))
                        ]
                    })
                ]
            }),
            value && /*#__PURE__*/ _jsx(Link, {
                to: `/delivery/programs/${programId}/${value}`,
                className: "text-xs text-primary underline",
                children: "View section"
            })
        ]
    });
}
function OutstandingItemsTable({ programId, items, checklistItems, availableSdsSections, locked, resource }) {
    const upsert = useUpsertCloseoutOutstandingItem();
    const remove = useDeleteCloseoutOutstandingItem();
    const [newDraft, setNewDraft] = useState(EMPTY_DRAFT);
    const [deleting, setDeleting] = useState(null);
    // Responsible-party options reuse the program's Roles roster (the same
    // ProgramRoleAssignment list the Roles surface renders) plus the current
    // user — matt round-3 #5. Resolved once here and threaded to every row.
    const { data: rosterData } = programRoleAssignmentHooks.useList({
        programId
    });
    const { user } = useAuth();
    const responsibleOptions = responsiblePartyOptions(rosterData?.items ?? [], user);
    const handleAdd = async ()=>{
        if (!newDraft.item.trim()) return;
        try {
            await upsert.mutateAsync({
                programId,
                outstandingItem: {
                    item: newDraft.item,
                    plannedCompletionDate: toTimestamp(newDraft.plannedCompletionDate),
                    comments: newDraft.comments,
                    sdsSection: newDraft.sdsSection,
                    responsibleParty: newDraft.responsibleParty
                }
            });
            setNewDraft(EMPTY_DRAFT);
        } catch  {
        // toast handled by hook
        }
    };
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        children: [
            /*#__PURE__*/ _jsx(SpecraCardHeader, {
                children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                    children: "Outstanding Items"
                })
            }),
            /*#__PURE__*/ _jsx(SpecraCardContent, {
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full text-sm",
                    "data-testid": Testids.closeout.outstandingTable,
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            children: /*#__PURE__*/ _jsx("tr", {
                                className: "border-b border-border text-left",
                                children: [
                                    'Item',
                                    'Planned Completion',
                                    'Comments',
                                    'SDS Link',
                                    'Responsible Party',
                                    'Resolved',
                                    ''
                                ].map((h)=>/*#__PURE__*/ _jsx("th", {
                                        className: "pb-2 pr-2 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground",
                                        children: h
                                    }, h))
                            })
                        }),
                        /*#__PURE__*/ _jsxs("tbody", {
                            children: [
                                items.map((row)=>/*#__PURE__*/ _jsx(OutstandingRow, {
                                        programId: programId,
                                        row: row,
                                        checklistItems: checklistItems,
                                        availableSdsSections: availableSdsSections,
                                        responsibleOptions: responsibleOptions,
                                        locked: locked,
                                        resource: resource,
                                        onRequestDelete: ()=>setDeleting(row)
                                    }, row.id)),
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "py-2 pr-2 align-top",
                                            children: /*#__PURE__*/ _jsx(ItemField, {
                                                value: newDraft.item,
                                                onChange: (next)=>setNewDraft((prev)=>({
                                                            ...prev,
                                                            item: next
                                                        })),
                                                checklistItems: checklistItems,
                                                disabled: locked
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "py-2 pr-2 align-top",
                                            children: /*#__PURE__*/ _jsx(Input, {
                                                type: "date",
                                                value: newDraft.plannedCompletionDate,
                                                onChange: (e)=>setNewDraft((prev)=>({
                                                            ...prev,
                                                            plannedCompletionDate: e.target.value
                                                        })),
                                                disabled: locked
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "py-2 pr-2 align-top",
                                            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                                value: newDraft.comments,
                                                onChange: (e)=>setNewDraft((prev)=>({
                                                            ...prev,
                                                            comments: e.target.value
                                                        })),
                                                rows: 3,
                                                disabled: locked
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "py-2 pr-2 align-top",
                                            children: /*#__PURE__*/ _jsx(SdsSectionField, {
                                                programId: programId,
                                                value: newDraft.sdsSection,
                                                onChange: (next)=>setNewDraft((prev)=>({
                                                            ...prev,
                                                            sdsSection: next
                                                        })),
                                                availableSdsSections: availableSdsSections,
                                                disabled: locked
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "py-2 pr-2 align-top",
                                            children: /*#__PURE__*/ _jsx(ResponsiblePartyField, {
                                                value: newDraft.responsibleParty,
                                                onChange: (next)=>setNewDraft((prev)=>({
                                                            ...prev,
                                                            responsibleParty: next
                                                        })),
                                                options: responsibleOptions,
                                                disabled: locked
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "py-2 pr-2 align-top"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "py-2 pr-2 text-right align-top",
                                            children: /*#__PURE__*/ _jsx(ActionGate, {
                                                action: "update",
                                                resource: resource,
                                                alsoDisabled: locked,
                                                children: /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "sm",
                                                    onClick: handleAdd,
                                                    disabled: !newDraft.item.trim() || upsert.isPending,
                                                    "data-testid": Testids.closeout.outstandingAddButton,
                                                    children: "Add"
                                                })
                                            })
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Outstanding Item",
                description: deleting ? `Remove "${deleting.item}" from the outstanding items list.` : '',
                onConfirm: async ()=>{
                    if (!deleting) return;
                    await remove.mutateAsync({
                        programId,
                        id: deleting.id
                    });
                    setDeleting(null);
                },
                isPending: remove.isPending,
                readOnly: locked
            })
        ]
    });
}
function OutstandingRow({ programId, row, checklistItems, availableSdsSections, responsibleOptions, locked, resource, onRequestDelete }) {
    const upsert = useUpsertCloseoutOutstandingItem();
    const [draft, setDraft] = useState(draftFromItem(row));
    const original = draftFromItem(row);
    const dirty = JSON.stringify(draft) !== JSON.stringify(original);
    const handleSave = async ()=>{
        try {
            await upsert.mutateAsync({
                programId,
                outstandingItem: {
                    id: row.id,
                    item: draft.item,
                    plannedCompletionDate: toTimestamp(draft.plannedCompletionDate),
                    comments: draft.comments,
                    sdsSection: draft.sdsSection,
                    responsibleParty: draft.responsibleParty,
                    resolved: draft.resolved
                }
            });
        } catch  {
        // toast handled by hook
        }
    };
    return /*#__PURE__*/ _jsxs("tr", {
        className: `border-b border-border/40 ${draft.resolved ? 'opacity-60' : ''}`,
        children: [
            /*#__PURE__*/ _jsx("td", {
                className: "py-2 pr-2 align-top",
                children: /*#__PURE__*/ _jsx(ItemField, {
                    value: draft.item,
                    onChange: (next)=>setDraft((prev)=>({
                                ...prev,
                                item: next
                            })),
                    checklistItems: checklistItems,
                    disabled: locked
                })
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "py-2 pr-2 align-top",
                children: /*#__PURE__*/ _jsx(Input, {
                    type: "date",
                    value: draft.plannedCompletionDate,
                    onChange: (e)=>setDraft((prev)=>({
                                ...prev,
                                plannedCompletionDate: e.target.value
                            })),
                    disabled: locked
                })
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "py-2 pr-2 align-top",
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    value: draft.comments,
                    onChange: (e)=>setDraft((prev)=>({
                                ...prev,
                                comments: e.target.value
                            })),
                    rows: 3,
                    disabled: locked
                })
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "py-2 pr-2 align-top",
                children: /*#__PURE__*/ _jsx(SdsSectionField, {
                    programId: programId,
                    value: draft.sdsSection,
                    onChange: (next)=>setDraft((prev)=>({
                                ...prev,
                                sdsSection: next
                            })),
                    availableSdsSections: availableSdsSections,
                    disabled: locked
                })
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "py-2 pr-2 align-top",
                children: /*#__PURE__*/ _jsx(ResponsiblePartyField, {
                    value: draft.responsibleParty,
                    onChange: (next)=>setDraft((prev)=>({
                                ...prev,
                                responsibleParty: next
                            })),
                    options: responsibleOptions,
                    disabled: locked
                })
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "py-2 pr-2 align-top",
                children: /*#__PURE__*/ _jsx("label", {
                    className: "flex items-center justify-center gap-1",
                    children: /*#__PURE__*/ _jsx(SpecraCheckbox, {
                        checked: draft.resolved,
                        onCheckedChange: (checked)=>setDraft((prev)=>({
                                    ...prev,
                                    resolved: Boolean(checked)
                                })),
                        disabled: locked,
                        "aria-label": "Mark outstanding item resolved",
                        "data-testid": Testids.closeout.outstandingResolvedCheckbox,
                        "data-ordinal-id": row.id
                    })
                })
            }),
            /*#__PURE__*/ _jsxs("td", {
                className: "py-2 pr-2 text-right align-top whitespace-nowrap",
                children: [
                    /*#__PURE__*/ _jsx(ActionGate, {
                        action: "update",
                        resource: resource,
                        alsoDisabled: locked,
                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            variant: "secondary",
                            onClick: handleSave,
                            disabled: !dirty || upsert.isPending,
                            "data-testid": Testids.closeout.outstandingSaveButton,
                            children: "Save"
                        })
                    }),
                    /*#__PURE__*/ _jsx(ActionGate, {
                        action: "update",
                        resource: resource,
                        alsoDisabled: locked,
                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            variant: "destructive",
                            onClick: onRequestDelete,
                            "data-testid": Testids.closeout.outstandingDeleteButton,
                            children: "Delete"
                        })
                    })
                ]
            })
        ]
    });
}
// Close-program prompt (matt round-3 #8). Closing removes the program from
// active views, so before it does, offer the PM a one-click "export &
// download": the SAME export that lands the artifact in Documents (#1110),
// plus a client-side download to their machine, so a completed checklist can't
// get stranded in the archive if they forget to grab a copy first.
function CloseProgramDialog({ open, onOpenChange, programId, onConfirmClose }) {
    const exportDownload = useExportAndDownloadCloseout();
    const [downloaded, setDownloaded] = useState(false);
    const handleDownload = async ()=>{
        try {
            await exportDownload.mutateAsync({
                programId
            });
            setDownloaded(true);
        } catch  {
        // toast handled by hook
        }
    };
    return /*#__PURE__*/ _jsx(AlertDialog, {
        open: open,
        onOpenChange: (next)=>{
            if (!next) setDownloaded(false);
            onOpenChange(next);
        },
        children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
            "data-testid": Testids.closeout.closeDialog,
            children: [
                /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                    children: [
                        /*#__PURE__*/ _jsx(AlertDialogTitle, {
                            children: "Close this program?"
                        }),
                        /*#__PURE__*/ _jsx(AlertDialogDescription, {
                            children: "Closing removes the program from active lists and rollups. It stays readable by its direct URL for audits, but it won't surface in your active views — so download a copy of the completed closeout checklist for your records before you close."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex items-center gap-2",
                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "outline",
                        onClick: handleDownload,
                        disabled: exportDownload.isPending,
                        iconLeft: downloaded ? /*#__PURE__*/ _jsx(Check, {}) : /*#__PURE__*/ _jsx(Download, {}),
                        "data-testid": Testids.closeout.closeDialogDownloadButton,
                        children: exportDownload.isPending ? 'Preparing…' : downloaded ? 'Downloaded (also saved to Documents)' : 'Export & download checklist'
                    })
                }),
                /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                    children: [
                        /*#__PURE__*/ _jsx(AlertDialogCancel, {
                            className: specraButtonVariants({
                                variant: 'secondary'
                            }),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(AlertDialogAction, {
                            className: specraButtonVariants({
                                variant: 'default'
                            }),
                            disabled: exportDownload.isPending,
                            onClick: onConfirmClose,
                            "data-testid": Testids.closeout.closeDialogConfirmButton,
                            children: "Close program"
                        })
                    ]
                })
            ]
        })
    });
}
